/**
 * search v1 — the 2024 client-side search. It searched the three
 * front-page teasers. It was honest about this in the placeholder.
 * v2 was going to search the archive; the archive is a filing cabinet
 * with opinions. This file stays because the archive page still
 * renders the box, and the box still works, on three strings.
 */

export interface SearchDoc {
  id: string;
  title: string;
  universe: string;
  body: string;
}

export interface SearchHit {
  doc: SearchDoc;
  score: number;
  why: string;
}

/** v1 corpus: whatever the page is currently showing. It is small. */
export function corpusFromPage(): SearchDoc[] {
  const docs: SearchDoc[] = [];
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(".wire-story"))) {
    const headline = el.querySelector(".wire-story__headline")?.textContent ?? "";
    const dek = el.querySelector(".wire-story__dek")?.textContent ?? "";
    const teaser = el.querySelector(".wire-story__teaser")?.textContent ?? "";
    docs.push({
      id: el.dataset.story ?? `story-${docs.length}`,
      title: headline,
      universe: document.documentElement.dataset.universe ?? "A-1",
      body: `${dek} ${teaser}`,
    });
  }
  return docs;
}

const STOPWORDS = new Set(["the", "a", "an", "of", "to", "in", "and", "or", "is", "was", "for", "on", "it"]);

export function searchV1(query: string, docs?: SearchDoc[]): SearchHit[] {
  const corpus = docs ?? corpusFromPage();
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t && !STOPWORDS.has(t));
  if (terms.length === 0) return [];

  const hits: SearchHit[] = [];
  for (const doc of corpus) {
    const hay = `${doc.title} ${doc.body}`.toLowerCase();
    let score = 0;
    let matched = 0;
    for (const term of terms) {
      const occurrences = hay.split(term).length - 1;
      if (occurrences > 0) {
        matched += 1;
        score += 1 + Math.min(3, occurrences - 1) * 0.4;
        if (doc.title.toLowerCase().includes(term)) score += 1.5;
      }
    }
    if (matched > 0) {
      hits.push({
        doc,
        score: Math.round(score * 100) / 100,
        why: `${matched}/${terms.length} terms, title bonus applied where earned`,
      });
    }
  }
  return hits.sort((a, b) => b.score - a.score).slice(0, 8);
}

export const V1_PLACEHOLDER = "Searches the front page. Only the front page. It says so.";

export function corpusSize(docs?: SearchDoc[]): number {
  return (docs ?? corpusFromPage()).length;
}
